import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from './MainLayout';
import { 
  CircularProgress,
  Box,
  Grid,
  LinearProgress,
  Fade
} from '@mui/material';
import { SortablePaginatedTable } from './common';
import CustomSelect from './CustomSelect';
import '../styles/Dashboard.css';
import '../styles/Contracts.css';
import '../styles/PointsDashboard.css';
import { Link } from 'react-router-dom';

export const PointsDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    monthLabels: [],
    data: [],
    summaryStats: {
      totalContracts: 0,
      totalPointsDeliveredByMonth: [0, 0, 0, 0, 0, 0],
      totalRecurringContracts: 0,
      totalProjectContracts: 0,
      totalPointsBalance: 0,
      totalPointsBurden: 0
    }
  });
  
  const [filterOptions, setFilterOptions] = useState({
    businessUnits: [],
    accountManagers: [],
    teamManagers: []
  });
  
  const [filters, setFilters] = useState({
    businessUnitId: 'all',
    accountManager: 'all',
    teamManager: 'all'
  });

  // Function to fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setTableLoading(true);
      
      // Build query string for filters
      const queryParams = new URLSearchParams();
      if (filters.businessUnitId && filters.businessUnitId !== 'all') {
        queryParams.append('businessUnitId', filters.businessUnitId);
      }
      if (filters.accountManager && filters.accountManager !== 'all') {
        queryParams.append('accountManager', filters.accountManager);
      }
      if (filters.teamManager && filters.teamManager !== 'all') {
        queryParams.append('teamManager', filters.teamManager);
      }
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      
      // Fetch data
      const response = await fetch(`${apiBaseUrl}/api/points-dashboard?${queryParams.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data: ${response.status}`);
      }
      
      const data = await response.json();
      setDashboardData(data);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(`Failed to load dashboard data: ${error.message}`);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [filters]);
  
  // Function to fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      const response = await fetch(`${apiBaseUrl}/api/points-dashboard/filter-options`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch filter options: ${response.status}`);
      }
      
      const options = await response.json();
      console.log('Filter options received:', options);
      setFilterOptions(options);
      
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Don't set error state here, as it's not critical for the page to function
    }
  }, []);
  
  // Initial data fetch
  useEffect(() => {
    // Fetch filter options first
    fetchFilterOptions();
    
    // Then fetch dashboard data
    fetchDashboardData();
  }, [fetchFilterOptions, fetchDashboardData]);
  
  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Define table columns
  const columns = [
    {
      id: 'contract_name',
      label: 'Contract Name',
      sortable: true,
      render: (row) => (
        <Link 
          to={`/contracts/${row.contract_id}`} 
          style={{ 
            color: '#333333',
            textDecoration: 'underline',
            fontWeight: 500
          }}
        >
          {row.contract_name}
        </Link>
      )
    },
    {
      id: 'external_id',
      label: 'Contract Number',
      sortable: true,
      render: (row) => (
        <Link 
          to={`/contracts/${row.contract_id}`} 
          style={{ 
            color: '#6200ee', 
            textDecoration: 'underline',
            fontWeight: 500
          }}
        >
          {row.external_id}
        </Link>
      )
    },
    {
      id: 'contract_type',
      label: 'Contract Type',
      sortable: true,
      format: (value) => value === 'recurring' ? 'Recurring' : 'Project'
    },
    {
      id: 'monthly_points_allotment',
      label: 'Monthly Points',
      sortable: true,
      render: (row) => {
        if (row.contract_type === 'project') return '-';
        return row.monthly_points_allotment;
      },
      cellProps: (value, row) => ({
        className: row.contract_type === 'project' ? 'project-cell' : '',
        sx: { fontWeight: row.contract_type === 'recurring' ? '600' : 'normal' }
      })
    }
  ];

  // Add month columns dynamically based on monthLabels
  if (dashboardData.monthLabels && dashboardData.monthLabels.length) {
    // Add columns for each month
    const monthColumns = [
      {
        id: 'points_5_months_ago',
        label: dashboardData.monthLabels[0] || 'Month 1',
        sortable: true,
        cellProps: (value, row) => ({
          className: getCellClassName(row, 5)
        })
      },
      {
        id: 'points_4_months_ago',
        label: dashboardData.monthLabels[1] || 'Month 2',
        sortable: true,
        cellProps: (value, row) => ({
          className: getCellClassName(row, 4)
        })
      },
      {
        id: 'points_3_months_ago',
        label: dashboardData.monthLabels[2] || 'Month 3',
        sortable: true,
        cellProps: (value, row) => ({
          className: getCellClassName(row, 3)
        })
      },
      {
        id: 'points_2_months_ago',
        label: dashboardData.monthLabels[3] || 'Month 4',
        sortable: true,
        cellProps: (value, row) => ({
          className: getCellClassName(row, 2)
        })
      },
      {
        id: 'points_last_month',
        label: dashboardData.monthLabels[4] || 'Month 5',
        sortable: true,
        cellProps: (value, row) => ({
          className: getCellClassName(row, 1)
        })
      },
      {
        id: 'points_this_month',
        label: dashboardData.monthLabels[5] || 'Month 6',
        sortable: true,
        cellProps: (value, row) => ({
          className: getCellClassName(row, 0)
        })
      }
    ];
    
    // Add month columns to the columns array
    columns.push(...monthColumns);
  }
  
  // Add balance and burden columns
  columns.push(
    {
      id: 'points_balance',
      label: 'Points Balance',
      sortable: true,
      cellProps: (value, row) => {
        const numValue = parseFloat(value || 0);
        return {
          sx: { 
            color: numValue < 0 ? '#2e7d32' : '#c62828',
            fontWeight: 600
          }
        };
      }
    },
    {
      id: 'points_burden',
      label: 'Points Burden',
      sortable: true,
      cellProps: (value, row) => {
        const numValue = parseFloat(value || 0);
        return {
          sx: { 
            color: numValue < 0 ? '#2e7d32' : '#c62828',
            fontWeight: 600
          }
        };
      }
    }
  );

  // Function to determine cell class based on contract type and points comparison
  function getCellClassName(contract, monthIndex) {
    // Handle missing data
    if (!contract) return '';
    
    // Project contracts have no color coding
    if (contract.contract_type === 'project') {
      return 'project-cell';
    }
    
    // Get the points for the specific month
    let pointsValue = 0;
    switch (monthIndex) {
      case 5: pointsValue = parseFloat(contract.points_5_months_ago || 0); break;
      case 4: pointsValue = parseFloat(contract.points_4_months_ago || 0); break;
      case 3: pointsValue = parseFloat(contract.points_3_months_ago || 0); break;
      case 2: pointsValue = parseFloat(contract.points_2_months_ago || 0); break;
      case 1: pointsValue = parseFloat(contract.points_last_month || 0); break;
      case 0: pointsValue = parseFloat(contract.points_this_month || 0); break;
      default: pointsValue = 0;
    }
    
    const monthlyAllotment = parseFloat(contract.monthly_points_allotment || 0);
    
    // Apply color coding based on comparison - only for recurring contracts with an allotment
    if (contract.contract_type === 'recurring' && monthlyAllotment > 0) {
      if (pointsValue >= monthlyAllotment) {
        return 'success-cell';
      } else {
        return 'warning-cell';
      }
    }
    
    // Default - no special class
    return '';
  }

  return (
    <MainLayout title="Dashboard">
      <div className="scrollable-content points-dashboard">
        {loading && !dashboardData?.data?.length ? (
          <div className="loading-container">
            <CircularProgress />
            <p>Loading dashboard data...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="points-dashboard-stats">
              {/* Total Contracts */}
              <div className="stat-card">
                <div className="stat-label">Total Contracts</div>
                <div className="stat-value">{Math.round(dashboardData?.summaryStats?.totalContracts || 0)}</div>
              </div>
              
              {/* Last Month's Points Delivered */}
              <div className="stat-card">
                <div className="stat-label">Last Month's Points Delivered</div>
                <div className="stat-value">
                  {Math.round(dashboardData?.summaryStats?.totalPointsDeliveredByMonth?.[4] || 0)}
                </div>
              </div>
              
              {/* This Month's Points Delivered */}
              <div className="stat-card">
                <div className="stat-label">This Month's Points Delivered</div>
                <div className="stat-value">
                  {Math.round(dashboardData?.summaryStats?.totalPointsDeliveredByMonth?.[5] || 0)}
                </div>
              </div>
              
              {/* This Month's Points in Working */}
              <div className="stat-card">
                <div className="stat-label">This Month's Points in Working</div>
                <div className="stat-value">{Math.round(dashboardData?.summaryStats?.totalPointsWorking || 0)}</div>
              </div>
              
              {/* Total Points Balance */}
              <div className="stat-card">
                <div className="stat-label">Total Points Balance</div>
                <div className="stat-value">
                  {Math.round(dashboardData?.summaryStats?.totalPointsBalance || 0)}
                </div>
              </div>
              
              {/* Total Points Burden */}
              <div className="stat-card">
                <div className="stat-label">Total Points Burden</div>
                <div className="stat-value">
                  {Math.round(dashboardData?.summaryStats?.totalPointsBurden || 0)}
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="filters-row">
              {/* Business Unit Filter */}
              <div>
                <div className="filter-label">Business Unit</div>
                <CustomSelect
                  name="businessUnitId"
                  value={filters.businessUnitId}
                  onChange={handleFilterChange}
                  options={[
                    { value: 'all', label: 'All Business Units' },
                    ...(filterOptions?.businessUnits || []).map(bu => ({
                      value: bu.quickbooks_business_unit_id,
                      label: bu.business_unit_name
                    }))
                  ]}
                />
              </div>
              
              {/* Account Manager Filter */}
              <div>
                <div className="filter-label">Account Manager</div>
                <CustomSelect
                  name="accountManager"
                  value={filters.accountManager}
                  onChange={handleFilterChange}
                  options={[
                    { value: 'all', label: 'All Account Managers' },
                    ...(filterOptions?.accountManagers || []).map(am => ({
                      value: am.account_manager,
                      label: am.account_manager_name
                    }))
                  ]}
                />
              </div>
              
              {/* Team Manager Filter */}
              <div>
                <div className="filter-label">Team Manager</div>
                <CustomSelect
                  name="teamManager"
                  value={filters.teamManager}
                  onChange={handleFilterChange}
                  options={[
                    { value: 'all', label: 'All Team Managers' },
                    ...(filterOptions?.teamManagers || []).map(tm => ({
                      value: tm.team_manager,
                      label: tm.team_manager_name
                    }))
                  ]}
                />
              </div>
            </div>

            {/* Color Legend */}
            <div className="legend-container">
              <div className="legend-title">Color Legend:</div>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="legend-color success-cell"></div>
                  <div className="legend-text">Meeting or exceeding monthly points allotment</div>
                </div>
                <div className="legend-item">
                  <div className="legend-color warning-cell"></div>
                  <div className="legend-text">Below monthly points allotment</div>
                </div>
                <div className="legend-item">
                  <div className="legend-color project-cell"></div>
                  <div className="legend-text">Project contract (no monthly allotment)</div>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: 'transparent', border: '1px solid #2e7d32', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(76, 175, 80, 0.2)' }}></div>
                  </div>
                  <div className="legend-text" style={{ color: '#2e7d32', fontWeight: 600 }}>Negative balance/burden (good)</div>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: 'transparent', border: '1px solid #c62828', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(244, 67, 54, 0.2)' }}></div>
                  </div>
                  <div className="legend-text" style={{ color: '#c62828', fontWeight: 600 }}>Positive balance/burden (bad)</div>
                </div>
              </div>
            </div>

            {/* Table Loading Indicator */}
            {tableLoading && (
              <Box sx={{ width: '100%', position: 'relative', mb: 1 }}>
                <Fade in={tableLoading} style={{ transitionDelay: tableLoading ? '300ms' : '0ms' }}>
                  <LinearProgress color="secondary" />
                </Fade>
              </Box>
            )}

            {/* Points Table */}
            <div className="contracts-table-container">
              <SortablePaginatedTable
                data={dashboardData?.data || []}
                columns={columns}
                rowsPerPage={20}
                emptyMessage={tableLoading ? 'Loading contracts...' : 'No contracts found with the selected filters.'}
                tableContainerProps={{ className: "enhanced-table" }}
                tableProps={{ stickyHeader: true }}
                initialOrderBy="contract_name"
                initialOrder="asc"
                stickyColumn="contract_name"
              />
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}; 